import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { meetingsApi } from '@/services/meetings';
import { adminApi } from '@/services/admin';
import { MeetingWithNotes, AdminStats } from '@/types';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { t } from '@/i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import toast from 'react-hot-toast';
import { Calendar, MapPin, Users, Activity, CreditCard, Mic, PenTool, FileText } from 'lucide-react';
import { DashboardStatsSkeleton, MeetingListSkeleton } from '@/components/skeletons';
import { EmptyState } from '@/components/ui/empty-state';
import { useNavigate } from 'react-router-dom';

const DashboardPage = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [recentMeetings, setRecentMeetings] = useState<MeetingWithNotes[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState<{
    totalMeetings: number;
    notesByType: Record<string, { count: number; percentage: number }>;
  } | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch user stats (for both regular users and admins)
      const userStatsResponse = await meetingsApi.getUserStats();
      setUserStats(userStatsResponse.stats);
      setRecentMeetings(userStatsResponse.stats.recentMeetings);

      // Fetch admin stats if user is admin
      if (user?.role === 'ADMIN') {
        const statsResponse = await adminApi.getStats();
        setStats(statsResponse.stats);
      }
    } catch (error: any) {
      toast.error(t('dashboard.failedToLoad'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5); // HH:MM
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('dashboard.title')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('dashboard.welcomeBack')}, {user?.fullName}!
          </p>
        </div>
        <Button onClick={logout} variant="outline">
          {t('auth.logout')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 lg:gap-6 2xl:gap-8">
        {/* Quick Actions */}
        <div className="p-6 bg-card rounded-lg border border-border">
          <h3 className="text-lg font-semibold mb-4">{t('dashboard.quickActions')}</h3>
          <div className="flex flex-col gap-2">
            <Link to="/new-meeting">
              <Button className="w-full" variant="default">
                {t('dashboard.newMeeting')}
              </Button>
            </Link>
            <Link to="/history">
              <Button className="w-full" variant="outline">
                {t('dashboard.viewHistory')}
              </Button>
            </Link>
            {user?.role === 'ADMIN' && (
              <Link to="/admin">
                <Button className="w-full" variant="secondary">
                  {t('dashboard.adminPanel')}
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* User Statistics */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('dashboard.yourMeetings')}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userStats?.totalMeetings || 0}</div>
            <p className="text-xs text-muted-foreground">
              {recentMeetings.length} {t('dashboard.inRecentHistory')}
            </p>
          </CardContent>
        </Card>

        {/* Note Types Breakdown */}
        <Card className="md:col-span-2 lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Użycie typów notatek</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Wizytówki</span>
                </div>
                <span className="text-sm font-medium">
                  {userStats?.notesByType?.BUSINESS_CARD?.count || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mic className="h-4 w-4 text-red-500" />
                  <span className="text-sm">Głosowe</span>
                </div>
                <span className="text-sm font-medium">
                  {userStats?.notesByType?.VOICE?.count || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PenTool className="h-4 w-4 text-purple-500" />
                  <span className="text-sm">Odręczne</span>
                </div>
                <span className="text-sm font-medium">
                  {userStats?.notesByType?.HANDWRITTEN?.count || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Tekstowe</span>
                </div>
                <span className="text-sm font-medium">
                  {userStats?.notesByType?.TEXT?.count || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Statistics - Only for Admins */}
      {user?.role === 'ADMIN' && loading && <DashboardStatsSkeleton />}
      {user?.role === 'ADMIN' && !loading && stats && (
        <div>
          <h2 className="text-2xl font-bold mb-4">{t('dashboard.systemStatistics')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 xl:gap-5 2xl:gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.totalUsers')}</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.users.total}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.users.active} {t('dashboard.active')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.allMeetings')}</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.meetings.total}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.meetings.recentlyCreated} {t('dashboard.thisMonth')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Wizytówki</CardTitle>
                <CreditCard className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.notes.byType.BUSINESS_CARD || 0}</div>
                <p className="text-xs text-muted-foreground">
                  w {stats.meetings.total} spotkaniach
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Głosowe</CardTitle>
                <Mic className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.notes.byType.VOICE || 0}</div>
                <p className="text-xs text-muted-foreground">
                  w {stats.meetings.total} spotkaniach
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Odręczne</CardTitle>
                <PenTool className="h-4 w-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.notes.byType.HANDWRITTEN || 0}</div>
                <p className="text-xs text-muted-foreground">
                  w {stats.meetings.total} spotkaniach
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tekstowe</CardTitle>
                <FileText className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.notes.byType.TEXT || 0}</div>
                <p className="text-xs text-muted-foreground">
                  w {stats.meetings.total} spotkaniach
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Top Users Section */}
          {stats.topUsers && stats.topUsers.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xl font-bold mb-4">Najaktywniejszi użytkownicy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 xl:gap-5">
                {stats.topUsers.slice(0, 6).map((topUser) => (
                  <Card key={topUser.userId}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">{topUser.fullName}</CardTitle>
                      <CardDescription className="text-xs">{topUser.email}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-muted-foreground">Spotkań:</span>
                        <span className="text-sm font-bold">{topUser.totalMeetings}</span>
                      </div>
                      <div className="space-y-1 mt-3">
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1">
                            <CreditCard className="h-3 w-3 text-blue-500" />
                            <span>Wizytówki</span>
                          </div>
                          <span className="font-medium">
                            {topUser.notesByType.BUSINESS_CARD?.count || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1">
                            <Mic className="h-3 w-3 text-red-500" />
                            <span>Głosowe</span>
                          </div>
                          <span className="font-medium">
                            {topUser.notesByType.VOICE?.count || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1">
                            <PenTool className="h-3 w-3 text-purple-500" />
                            <span>Odręczne</span>
                          </div>
                          <span className="font-medium">
                            {topUser.notesByType.HANDWRITTEN?.count || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3 text-green-500" />
                            <span>Tekstowe</span>
                          </div>
                          <span className="font-medium">
                            {topUser.notesByType.TEXT?.count || 0}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Meetings */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">{t('dashboard.recentMeetings')}</h2>
          <Link to="/history">
            <Button variant="ghost">{t('dashboard.viewAll')}</Button>
          </Link>
        </div>

        {loading ? (
          <MeetingListSkeleton count={3} />
        ) : recentMeetings.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title={t('dashboard.noRecentMeetings')}
            description="Rozpocznij swoją pracę od stworzenia pierwszego spotkania. Dodaj notatki, wizytówki i nagrania."
            actionLabel={t('dashboard.createFirstMeeting')}
            onAction={() => navigate('/new-meeting')}
            secondaryActionLabel="Zobacz historię"
            onSecondaryAction={() => navigate('/history')}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 xl:gap-5">
            {recentMeetings.map((meeting) => {
              const meetingTitle = meeting.person_name && meeting.company_name
                ? `${meeting.person_name} - ${meeting.company_name}`
                : meeting.person_name || meeting.company_name || 'Spotkanie bez nazwy';

              return (
                <Link to={`/meeting/${meeting.meeting_id}`} key={meeting.meeting_id}>
                  <Card className="hover:bg-card/80 transition-colors cursor-pointer hover-lift smooth-appear">
                    <CardHeader>
                      <CardTitle className="text-base">
                        {meetingTitle}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-2">
                        <Calendar className="h-3 w-3" />
                        {formatDate(meeting.meeting_date)} {formatTime(meeting.meeting_time) && `• ${formatTime(meeting.meeting_time)}`}
                        {meeting.location && (
                          <>
                            <MapPin className="h-3 w-3 ml-2" />
                            {meeting.location}
                          </>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex justify-between items-center">
                        <Badge variant="outline">
                          {meeting.notes?.length || 0} {meeting.notes?.length === 1 ? t('dashboard.note') : t('dashboard.notes')}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(meeting.created_at).toLocaleDateString('pl-PL')}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
